import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import { useCurrentUser } from "@/api/queries/me";

export type DeviceRead = {
  id: string;
  department_id: string;
  department_name: string | null;
  assigned_user_id: string | null;
  assigned_user_name: string | null;
  vendor: string;
  model: string;
  serial_number: string;
  honeywell_device_id: string | null;
  firmware_version: string | null;
  status: string;
  last_seen_at: string | null;
  created_at: string;
  updated_at: string;
};

export function useDevices() {
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const me = useCurrentUser();
  const deptId = selectedDeptId ?? me.data?.department?.id ?? null;
  return useQuery({
    queryKey: ["devices", deptId],
    queryFn: () => api.get<DeviceRead[]>("/devices"),
    enabled: !!deptId,
  });
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      serial_number: string;
      model?: string;
      vendor?: string;
      assigned_user_id?: string | null;
    }) => api.post<DeviceRead>("/devices", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices"] }),
  });
}

export function useUpdateDevice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      assigned_user_id?: string | null;
      status?: string;
    }) => api.patch<DeviceRead>(`/devices/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["device", id] });
    },
  });
}

export function useUnassignDevice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<DeviceRead>(`/devices/${id}/unassign`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["device", id] });
    },
  });
}

export type GatewayKeyResponse = {
  sensor_device_id: string;
  api_key: string;
  message: string;
};

export function useRotateDeviceKey(id: string) {
  return useMutation({
    mutationFn: () =>
      api.post<GatewayKeyResponse>(`/gateway/devices/${id}/rotate-key`),
  });
}
